import React from 'react';
import {Link} from 'react-router-dom';
import BottomLink from '../../components/BottomLink';

function Leadgen() {
  return (
    <div className="bww-page page-work"><div className="bww-section section-case-studies">
    <div className="case-study">
      <h2>Lead-gen websites
      <Link className="cs-nav cs-nav-prev" to="/case-rnf">&#9650;</Link>
      <Link className="cs-nav cs-nav-next" to="/case-quickenloans">&#9660;</Link>
      </h2>
      <div class="bww-legend">

      <p><b>Project:</b> Multiple lead-gen websites (SEO acquisition + conversion sites for consumer finance / mortgage purchase &amp; refinance)</p>
      <p><b>My role:</b> Staff/Principal Frontend Engineer + Technical Lead (architect, delivery lead, mentor)</p>
      <p><b>Team:</b> Led mixed-seniority frontend, QA and PMO engineers, plus cross-functional partners: SEO, product, design, content, data analytics</p>
      </div>

      <h3>Context</h3>
<p>A portfolio of SEO-optimized informational sites designed to capture organic search demand and convert that traffic into qualified leads for loan originators—delivered repeatedly across multiple initiatives using a shared platform foundation.</p>
<p>I owned the frontend architecture and execution strategy end-to-end: prototyping the technical approach, aligning stakeholders, then leading teams while partnering closely with design and product owners to ship multiple sites quickly and safely.</p>
<p>These projects lived at the intersection of content scale and conversion pressure: we needed hundreds to thousands of SEO pages, strong technical SEO, and a publishing workflow that didn’t require engineers to babysit every update. In parallel, the business needed consistent conversion patterns—clear CTAs, fast load times, and measurable outcomes—without locking us into brittle, one-off implementations.</p>
<p>A key milestone was modernizing an existing monolithic CMS-rendered site that had accumulated idiosyncratic behavior and performance drag, while preserving (and ultimately improving) search equity.</p>

      <h3>Hard constraints:</h3>
      <ul>
        <li><b>SEO could not regress</b>: needed to preserve rankings, indexation, metadata parity, and URL integrity during launches and migrations.</li>

<li><b>Performance was a requirement</b>: CWV and real-user speed had to materially improve.</li>

<li><b>Editorial velocity mattered</b>: content teams needed predictable templates, safe modularity, and reliable previews.</li>

<li><b>Multiple teams, multiple sites</b>: parallel delivery required strong standards, onboarding, and guardrails.</li>

<li><b>Compliance + quality gates</b>: accessibility, analytics, consent, and QA needed to be consistent and auditable.</li>

      </ul>

      <h3>What I did: decisions + executions</h3>

      <p><b><i>Decisions (the architecture and operating model)</i></b></p>

<ul>
  <li><b>Prototyped a headless architecture</b>: Gatsby frontend backed by a customized WordPress CMS surfaced through GraphQL, combining WordPress’s editorial strengths with a frontend built for performance and scale.</li>
  
  <li><b>Designed for reuse and repeatability</b>: I engineered a shared component library/platform so successive sites could inherit proven patterns (templates, UI modules, analytics hooks, accessibility defaults) instead of reinventing them.</li>
  
  <li><b>Made performance and SEO first-class</b>: established budgets and non-negotiables around CWV, asset strategy, caching, and structured content—so “later optimization” didn’t become “never.”</li>
</ul>

<p><b><i>Executions (how it shipped in the real world)</i></b></p>

<ul>
  <li><b>Built the reference implementation</b>: validated content modeling, page composition, routing, GraphQL query patterns, build strategy, and deployment shape before scaling the approach across teams.</li>
  
  <li><b>Created scalable templates + content blocks</b>: modular sections that empowered content teams to publish quickly while keeping markup consistent, accessible, and SEO-friendly.</li>
  
  <li><b>Standardized CI/CD + quality gates</b>: repeatable workflows for builds, previews, linting/type checks, testing expectations, and release hygiene—reducing “tribal knowledge” risk.</li>
  
  <li><b>Operationalized CI/CD as a self-serve publishing capability</b>: I led backend engineers in building a robust CI/CD pipeline (builds, previews, promotion, and release hygiene) and configured a proprietary frontend plugin that let content authors trigger builds themselves. That enabled each site to deploy with fresh content on-demand—without engineering as the gatekeeper—while still enforcing quality and compliance checks.</li>
  
  <li><b>Led migrations without losing search equity</b>: redirect strategy, canonical rules, metadata parity, sitemap/indexation checks, and rollout sequencing to protect rankings while improving speed.</li>
  
  <li><b>Operational leadership + mentorship</b>: coached junior and senior engineers, documented playbooks, unblocked complex issues, and created a delivery rhythm that let multiple squads execute independently.</li>
  
  <li><b>Cross-functional alignment</b>: partnered with design and product owners to balance conversion UX with SEO realities, and worked closely with QA/PMO to keep delivery predictable under tight timelines.</li>
</ul>

<h3>Outcomes</h3>

<ul>
  <li><b>A repeatable lead-gen platform</b> that supported multiple SEO sites with consistent standards and faster delivery.</li>
  
  <li><b>Self-serve publishing at scale</b>: content teams could trigger production-grade builds using fresh CMS content through an author-facing plugin, dramatically reducing time-to-publish and eliminating a common dependency on engineering for routine releases.</li>
  
  <li><b>Publishing became a force multiplier</b>: content teams could scale page production confidently within guardrails, with fewer engineering bottlenecks.</li>
  
  <li><b>A modernization path</b> that upgraded a legacy monolith into a faster, more maintainable architecture while protecting and growing organic performance.</li>
</ul>


<h3>Results</h3>

<ul>
<li><b>~300% improvement in Core Web Vitals performance scores</b> after incorporating my headless architecture and enforcing performance budgets.</li>

<li><b>~650% organic traffic growth</b> over two years on a representative site, driven by improved technical SEO fundamentals plus sustained content expansion.</li>
  <li><b>Portfolio-wide perational wins</b>:
    <ul>
<li>Reduced release coordination overhead by shifting routine builds from “engineer-operated” to “author-initiated with guardrails.”</li>

<li>Increased publishing cadence while maintaining consistent QA/performance/SEO gates. Reduced duplicated effort via shared components and standardized templates</li>

<li>Improved QA throughput and consistency across multiple properties</li>

<li>Faster onboarding and parallel delivery with less architectural drift</li>
</ul>
  </li> 
</ul>

      <p><b>Tech stack:</b> Decoupled architecture; React, JavaScript/TypeScript, Gatsby.js, WordPress, GraphQL, AWS S3/CloudFront/Lambda, CloudFlare, MariaDB, Storybook, testing libraries</p>

      
    </div>
    <BottomLink to="/case-quickenloans" label="Quicken Loans" />
    </div>
    </div>
  );
}

export default Leadgen;
